import Link from 'next/link'
import { PhotoIcon } from '@heroicons/react/24/outline'
import { formatDate } from '@/date'
import { getBlogCategoryHref } from '@/lib/blogCategoryPaths'

export const BlogSearchResult = ({
  objectId,
  thumbnail: _thumbnail,
  title,
  creators,
  productUrl,
  categories,
  dateAdded,
}: BlogSearchResultProps) => {
  // Extract URL stub from productUrl if it's a full URL
  // Blog URLs are typically /blog/{urlStub}
  const getBlogUrl = () => {
    if (!productUrl) return '#'
    // If it's already a relative URL starting with /blog, use it
    if (productUrl.startsWith('/blog/')) {
      return productUrl
    }
    // If it's a full URL, extract the path
    try {
      const url = new URL(productUrl)
      return url.pathname
    } catch {
      // If it's not a valid URL, assume it's already a path
      return productUrl.startsWith('/') ? productUrl : `/blog/${productUrl}`
    }
  }

  // Extract URL stub from productUrl for thumbnail
  const getUrlStub = () => {
    const blogUrl = getBlogUrl()
    // Extract the last part of the path (the URL stub)
    const parts = blogUrl.split('/').filter(Boolean)
    return parts[parts.length - 1] || null
  }

  // Get thumbnail URL - always construct from URL stub since thumbnail_url is set to "vi.jpg" by default
  const getThumbnailUrl = () => {
    const urlStub = getUrlStub()
    if (urlStub) {
      // Construct from URL stub (ignore the default "vi.jpg" thumbnail_url value)
      return `https://vinc.gumlet.io/gallery/blog/thumbs/${urlStub}.jpg?w=150&dpr=2`
    }
    return null
  }

  // Convert Unix timestamp to date string if needed
  const getDateString = () => {
    if (!dateAdded) return null
    // If it's a Unix timestamp (number)
    if (typeof dateAdded === 'number') {
      return new Date(dateAdded * 1000).toISOString()
    }
    // If it's already a date string
    return dateAdded
  }

  const dateString = getDateString()
  const blogUrl = getBlogUrl()

  // Handle categories - they might be stored as an array of characters (from ToArray() on a string)
  // or as an array of category names. Join them to get the full category name.
  const getCategoryName = () => {
    if (!categories || categories.length === 0) return null
    // If categories is an array of characters (from ToArray() on a string), join them
    // Otherwise, use the first category name
    const category = categories.join('')
    return category || null
  }

  const categoryName = getCategoryName()
  const categoryHref = getBlogCategoryHref(categoryName)
  const thumbnailUrl = getThumbnailUrl()

  return (
    <div
      key={objectId}
      className="flex py-4 px-4 border-b-[1px] border-gray-300 hover:bg-gray-50 transition-colors"
    >
      <div className="relative group flex-shrink-0">
        <div className="flex items-center justify-center w-[120px] h-[68px] md:w-[150px] md:h-[84px] rounded-sm aspect-[16/9] bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-200 overflow-hidden">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={title || 'Blog post'}
              className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
              onError={e => {
                e.currentTarget.style.display = 'none'
                const nextSibling = e.currentTarget
                  .nextElementSibling as HTMLElement
                if (nextSibling) {
                  nextSibling.style.display = 'flex'
                }
              }}
            />
          ) : null}
          <div
            className={`flex flex-col items-center justify-center w-full h-full text-gray-400 text-xs ${thumbnailUrl ? 'hidden' : 'flex'}`}
            style={{ display: thumbnailUrl ? 'none' : 'flex' }}
          >
            <PhotoIcon className="w-6 h-6 mb-1" />
          </div>
        </div>
      </div>
      <div className="ml-4 flex-1">
        <div>
          <Link
            className="text-2xl font-bold text-vi-red underline hover:no-underline"
            href={blogUrl}
          >
            {title}
          </Link>
        </div>
        <div className="text-gray-500 text-sm mt-1">
          {creators || 'Vanishing Inc.'}
          {dateString && ` - ${formatDate(dateString)}`}
        </div>
        {categoryName && categoryHref && (
          <div className="text-gray-500 text-sm mt-1">
            Category:{' '}
            <Link
              href={categoryHref}
              className="text-vi-red inline underline hover:no-underline"
            >
              {categoryName}
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

interface BlogSearchResultProps {
  objectId?: string
  thumbnail?: string
  title?: string
  creators?: string
  productUrl?: string
  categories?: string[]
  dateAdded?: number | string
}
